// Activity events feed for the per-item detail panel Activity tab.
// Pulls planner_activity_event rows for (itemKind, itemId) and renders
// them in chronological order. Each row maps the verb to a sentence
// like "Alice set status to On track".

import { tpApi } from "../api";
import { useResource } from "../hooks";
import { Avatar } from "./Avatar";
import { CommentThread } from "./CommentThread";
import { DC } from "../sheets/createSlideOver/primitives";
import { formatDateShort } from "../utils";

interface ActivityFeedProps {
  itemKind: "rock" | "milestone" | "todo" | "subtask" | "issue";
  itemId: string;
}

// Render a sentence from (verb, payload). Verb conventions match what
// the server writes via recordActivity().
function renderVerb(verb: string, payload: Record<string, unknown> | null): string {
  if (verb === "created") return "created this item";
  if (verb === "updated") return "edited the details";
  if (verb === "archived") return "archived it";
  if (verb === "deleted") return "deleted it";
  if (verb === "raised") return "raised this opportunity";
  if (verb === "commented") return "posted a comment";
  if (verb === "primary-owner-changed") return "reassigned the owner";
  if (verb.startsWith("status:")) {
    return `set status to ${verb.slice("status:".length).replace("_", " ")}`;
  }
  if (verb.startsWith("status-update:")) {
    const rag = verb.slice("status-update:".length);
    return `posted a ${rag.replace("_", " ")} weekly update`;
  }
  if (verb.startsWith("participant:")) {
    const action = verb.slice("participant:".length);
    return `${action === "add" ? "added" : "removed"} a participant`;
  }
  if (verb === "converted-to-todo") return "converted this to a to-do";
  if (verb === "converted-to-rock") return "converted this to a rock";
  void payload;
  return verb;
}

export function ActivityFeed({ itemKind, itemId }: ActivityFeedProps) {
  const events = useResource(() => tpApi.listActivity(itemKind, itemId), [itemKind, itemId]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <section>
        <div
          style={{
            font: "700 10px/1 var(--bsp-font-sans)",
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: DC.grey,
            marginBottom: 10,
          }}
        >
          Events · {events.data?.length ?? 0}
        </div>
        {events.loading ? (
          <p style={{ color: DC.grey, fontSize: 13 }}>Loading…</p>
        ) : (events.data ?? []).length === 0 ? (
          <p style={{ color: DC.grey, fontSize: 13 }}>No events recorded yet.</p>
        ) : (
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: 0,
              display: "flex",
              flexDirection: "column",
              gap: 6,
              position: "relative",
            }}
          >
            {(events.data ?? []).map((e) => (
              <li
                key={e.id}
                style={{
                  display: "flex",
                  gap: 10,
                  padding: "8px 10px",
                  background: DC.surface,
                  border: `1px solid ${DC.border}`,
                  borderRadius: 6,
                  alignItems: "center",
                }}
              >
                <Avatar owner={{ displayName: e.actorDisplayName, email: null }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, lineHeight: 1.4 }}>
                    <strong>{e.actorDisplayName ?? "Someone"}</strong>{" "}
                    <span style={{ color: DC.grey }}>{renderVerb(e.verb, e.payload)}</span>
                  </div>
                </div>
                <span
                  style={{
                    fontSize: 11,
                    color: DC.grey,
                    fontFamily: "var(--bsp-font-mono)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatDateShort(e.createdAt)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* The comment thread doubles as the activity composer — Drillcut
          collapses the two surfaces together so a comment naturally
          appears in the timeline. */}
      <CommentThread itemKind={itemKind} itemId={itemId} />
    </div>
  );
}
